const ACCESS_KEY = "wallboards_sso_access_token";
const REFRESH_KEY = "wallboards_sso_refresh_token";
const LOGOUT_KEY = "wallboards_sso_logout_token";
const REALM_KEY = "wallboards_sso_realm";

const REFRESH_INTERVAL_MS = 4 * 60 * 1000;

let refreshTimer = null;
let refreshInFlight = null;

export function isBackend2Sso() {
  return String(process.env.VUE_APP_AUTH_MODE || "").toLowerCase() === "backend2-sso";
}

export function isJwt(token) {
  if (!token || typeof token !== "string") {
    return false;
  }
  const parts = token.split(".");
  return parts.length === 3 && parts.every((part) => part.length > 0);
}

function decodeJwtPayload(token) {
  if (!isJwt(token)) {
    return null;
  }
  try {
    const segment = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = segment + "=".repeat((4 - (segment.length % 4)) % 4);
    return JSON.parse(atob(padded));
  } catch (e) {
    return null;
  }
}

/** Payload `exp` only. Missing or unreadable expiry counts as expired. */
export function isAccessTokenExpired(token) {
  const payload = decodeJwtPayload(token);
  if (!payload || typeof payload.exp !== "number") {
    return true;
  }
  return payload.exp * 1000 <= Date.now();
}

export function apiOrigin() {
  return (
    process.env.VUE_APP_API_ORIGIN ||
    "http://localhost:1337/api"
  ).replace(/\/$/, "");
}

export function authRealm() {
  return process.env.VUE_APP_KEYCLOACK_REALM || "improvers";
}

export function isSsoCallbackPath(pathname = window.location.pathname) {
  return pathname === "/auth/sso" || pathname.endsWith("/auth/sso");
}

function readStorage(key) {
  try {
    return localStorage.getItem(key) || "";
  } catch (e) {
    return "";
  }
}

function writeStorage(key, value) {
  try {
    if (!value) {
      localStorage.removeItem(key);
      return;
    }
    localStorage.setItem(key, value);
  } catch (e) {
    // ignore quota / private mode
  }
}

export function getStoredAccessToken() {
  return readStorage(ACCESS_KEY);
}

export function getStoredRefreshToken() {
  return readStorage(REFRESH_KEY);
}

export function hasSsoSession() {
  return Boolean(getStoredAccessToken() && getStoredRefreshToken());
}

export function saveSsoSession(session) {
  const data = session || {};
  writeStorage(ACCESS_KEY, data.accessToken || "");
  writeStorage(REFRESH_KEY, data.refreshToken || "");
  writeStorage(LOGOUT_KEY, data.logoutToken || "");
  writeStorage(REALM_KEY, data.realm || authRealm());
}

export function clearSsoSession() {
  writeStorage(ACCESS_KEY, "");
  writeStorage(REFRESH_KEY, "");
  writeStorage(LOGOUT_KEY, "");
  writeStorage(REALM_KEY, "");
}

export function restoreSsoSession() {
  return getStoredAccessToken();
}

export function currentReturnPath() {
  const url = new URL(window.location.href);
  url.searchParams.delete("token");
  url.searchParams.delete("accessToken");
  url.searchParams.delete("logoutToken");
  url.searchParams.delete("realm");
  url.searchParams.delete("return_path");
  return `${url.pathname}${url.search}` || "/";
}

export function startBackend2Login() {
  const realm = authRealm();
  const returnPath = encodeURIComponent(currentReturnPath());
  window.location.href = `${apiOrigin()}/auth/start-login/${realm}?client=wallboards&return_path=${returnPath}`;
}

function isAuthUrl(url) {
  return /\/auth\/(finish-login|refresh|start-login|logout)\b/.test(url);
}

export async function finishBackend2Login(session) {
  const data = session || {};
  const realm = data.realm;
  const accessToken = data.accessToken;
  const logoutToken = data.logoutToken;
  const path = `${apiOrigin()}/auth/finish-login/${encodeURIComponent(realm)}?token=${encodeURIComponent(accessToken)}`;
  const response = await fetch(path);
  const body = await response.json().catch(() => null);
  const payload = (body && body.data) || body || {};
  const nextAccess = payload.access_token || payload.accessToken;
  const nextRefresh = payload.refresh_token || payload.refreshToken;
  if (!response.ok || !nextAccess) {
    const message =
      (body && body.error && body.error.message) ||
      (body && body.message) ||
      `finish-login failed (${response.status})`;
    throw new Error(message);
  }

  saveSsoSession({
    accessToken: nextAccess,
    refreshToken: nextRefresh,
    logoutToken,
    realm,
  });

  return {
    accessToken: nextAccess,
    refreshToken: nextRefresh,
  };
}

export async function refreshSsoTokens() {
  if (refreshInFlight) {
    return refreshInFlight;
  }

  refreshInFlight = (async () => {
    const refreshToken = getStoredRefreshToken();
    if (!refreshToken) {
      return false;
    }

    let response;
    try {
      response = await fetch(`${apiOrigin()}/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
    } catch (e) {
      return null;
    }

    const body = await response.json().catch(() => null);
    const payload = (body && body.data) || body || {};
    const nextAccess = payload.access_token || payload.accessToken;
    const nextRefresh = payload.refresh_token || payload.refreshToken;
    if (!response.ok || !nextAccess) {
      if (
        response.status === 401 ||
        response.status === 403 ||
        response.status === 404
      ) {
        return false;
      }
      return null;
    }

    saveSsoSession({
      accessToken: nextAccess,
      refreshToken: nextRefresh,
      logoutToken: readStorage(LOGOUT_KEY),
      realm: readStorage(REALM_KEY) || authRealm(),
    });
    return nextAccess;
  })().finally(() => {
    refreshInFlight = null;
  });

  return refreshInFlight;
}

export function startSsoRefresh(onAccessToken) {
  stopSsoRefresh();
  refreshTimer = setInterval(async () => {
    const next = await refreshSsoTokens();
    if (next && typeof onAccessToken === "function") {
      onAccessToken(next);
    }
    if (next === false) {
      clearSsoSession();
      startBackend2Login();
    }
  }, REFRESH_INTERVAL_MS);
}

export function stopSsoRefresh() {
  if (refreshTimer) {
    clearInterval(refreshTimer);
    refreshTimer = null;
  }
}

export function shouldRetryUnauthorized(url) {
  return isBackend2Sso() && url && !isAuthUrl(url);
}

export { ACCESS_KEY, REFRESH_INTERVAL_MS };
